/**
 * pages/Monthly.js — the in-app "monthly edition mirror".
 *
 * Deep-link target for the monthly email CTA. Renders the SAME structural
 * layout as the email:
 *   • RICH → lightly-quantitative teaser (net worth / debt / investing /
 *              savings-rate direction) + reflection prompt + two CTAs.
 *   • THIN → what-we-saw + what-you'd-unlock preview.
 *   • EMPTY → onboarding first: three concrete steps to unlock the recap.
 *   • REENGAGE → gentle "we're here whenever you're ready" card.
 *
 * State (RICH / THIN / EMPTY / REENGAGE) is decided by the backend
 * (`build_and_render` in lib/monthly_brief/sender.py); the frontend just
 * branches on `preview.state`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDownRight, Minus,
  BookOpen, LayoutDashboard,
  PlusCircle, Compass, User, Sparkles,
} from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mirror the compact currency formatter used in the email renderer so the
// in-app THIN view matches. RICH intentionally hides raw money.
function fmtCurrency(amount, currency = 'INR') {
  if (amount == null) return '—';
  const sym = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', SGD: 'S$' }[String(currency).toUpperCase()] || '';
  if ((currency || 'INR').toUpperCase() === 'INR') {
    if (amount >= 1e7) return `${sym}${(amount / 1e7).toFixed(1)}Cr`;
    if (amount >= 1e5) return `${sym}${(amount / 1e5).toFixed(1)}L`;
    if (amount >= 1e3) return `${sym}${(amount / 1e3).toFixed(1)}k`;
    return `${sym}${Math.round(amount)}`;
  }
  if (amount >= 1e6) return `${sym}${(amount / 1e6).toFixed(1)}M`;
  if (amount >= 1e3) return `${sym}${(amount / 1e3).toFixed(1)}k`;
  return `${sym}${Math.round(amount)}`;
}

function monthLabelFromStr(monthStr) {
  if (!monthStr) return '';
  const [y, m] = monthStr.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export default function Monthly() {
  usePageTitle('Monthly recap');
  const { session } = useAuth();
  const token = session?.access_token;
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get('month');  // 'YYYY-MM' or null

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // { state, subject, brief, ... }

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `${BACKEND_URL}/api/monthly-brief/preview?include_html=false${monthParam ? `&month=${monthParam}` : ''}`;
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        console.warn('Monthly load failed:', e?.message);
        toast.error('Could not load your monthly recap.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, monthParam]);

  const monthStr = preview?.month || monthParam || '';
  const monthLabel = useMemo(() => monthLabelFromStr(monthStr), [monthStr]);
  const reflectionUrl = monthStr ? `/reflections?month=${monthStr}&tab=monthly` : '/reflections';
  const dashboardUrl  = '/dashboard';

  if (loading) {
    return (
      <div className="space-y-4" data-testid="monthly-loading">
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-48 rounded-3xl" />
      </div>
    );
  }

  if (!preview) return null;

  const state = preview.state;
  const brief = preview.brief || {};

  return (
    <div className="space-y-5" data-testid="monthly-root">
      <div>
        <div className="text-[11px] uppercase tracking-[2px] text-gold" data-testid="monthly-label">
          {monthLabel ? `${monthLabel.toUpperCase()} · MONTHLY RECAP` : 'MONTHLY RECAP'}
        </div>
        <h1 className="font-display text-2xl font-semibold mt-1" data-testid="monthly-heading">
          {preview.subject || 'Your monthly recap'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{preview.preheader}</p>
      </div>

      {state === 'rich' && (
        <RichView
          brief={brief}
          monthLabel={monthLabel}
          reflectionUrl={reflectionUrl}
          dashboardUrl={dashboardUrl}
        />
      )}
      {state === 'thin' && (
        <ThinView
          brief={brief}
          monthStr={monthStr}
          reflectionUrl={reflectionUrl}
          dashboardUrl={dashboardUrl}
        />
      )}
      {state === 'empty' && <EmptyView brief={brief} />}
      {state === 'reengage' && <ReengageView />}
      {state === 'off' && (
        <Card className="rounded-2xl border-dashed" data-testid="monthly-off">
          <CardContent className="p-8 text-center">
            <div className="text-sm">You&rsquo;ve turned off monthly recaps. You can re-enable them in Settings.</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- RICH — lightly-quantitative teaser (mirrors the email) ---------------
function RichView({ brief, monthLabel, reflectionUrl, dashboardUrl }) {
  const data = brief.data || {};

  return (
    <>
      <Card className="rounded-3xl border-border/70" data-testid="monthly-signals">
        <CardContent className="p-5 sm:p-6">
          <div className="text-[11px] uppercase tracking-[2px] text-gold mb-4">
            The shape of {monthLabel}
          </div>
          <div className="space-y-3 divide-y divide-border/50">
            <SignalRow
              label="Net worth"
              deltaPct={data.net_worth_delta_pct}
              baseline={data.net_worth != null && data.net_worth_delta_pct == null}
              upIsGood
              testid="signal-net-worth"
            />
            {data.total_debt != null && data.total_debt > 0 && (
              <SignalRow
                label="Debt"
                deltaPct={data.total_debt_delta_pct}
                baseline={data.total_debt_delta_pct == null}
                upIsGood={false}
                testid="signal-debt"
              />
            )}
            {data.monthly_investment != null && data.monthly_investment > 0 && (
              <InvestingRow deltaPct={data.investment_delta_pct} testid="signal-investing" />
            )}
            {data.savings_rate_pct != null && (
              <SavingsRow rate={data.savings_rate_pct} testid="signal-savings" />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
            Exact numbers, category detail, and goal progress live in your dashboard.
          </p>
        </CardContent>
      </Card>

      {brief.question?.text && (
        <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg" data-testid="monthly-question">
          <CardContent className="p-6 space-y-3">
            <div className="text-[11px] uppercase tracking-[2px] text-gold">Reflection prompt</div>
            <div className="font-display text-xl leading-snug">{brief.question.text}</div>
            <p className="text-sm text-primary-foreground/70">
              Save your answer to your private Reflections journal — no reply needed.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col items-center gap-3 pt-1">
        <Button asChild size="lg" className="rounded-xl" data-testid="monthly-cta-reflection">
          <a href={reflectionUrl}>
            <BookOpen className="h-4 w-4 mr-2" />
            Record your reflection
          </a>
        </Button>
        <a
          href={dashboardUrl}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          data-testid="monthly-cta-dashboard"
        >
          <LayoutDashboard className="h-3.5 w-3.5" />
          Open your dashboard
        </a>
      </div>
    </>
  );
}

// --- Signal row primitives -------------------------------------------------
function directionMeta(pct, upIsGood) {
  if (pct == null) return { arrow: Minus, label: 'held steady', tone: 'muted' };
  if (Math.abs(pct) < 0.5) return { arrow: Minus, label: 'held steady', tone: 'muted' };
  if (pct > 0) {
    return { arrow: ArrowUpRight, label: `up ${pct.toFixed(1)}%`, tone: upIsGood ? 'positive' : 'negative' };
  }
  return { arrow: ArrowDownRight, label: `down ${Math.abs(pct).toFixed(1)}%`, tone: upIsGood ? 'negative' : 'positive' };
}

function toneClasses(tone) {
  if (tone === 'positive') return 'text-emerald-700 dark:text-emerald-400';
  if (tone === 'negative') return 'text-rose-700 dark:text-rose-400';
  return 'text-muted-foreground';
}

function SignalRow({ label, deltaPct, baseline, upIsGood, testid }) {
  if (baseline) {
    return (
      <div className="flex items-center justify-between pt-3 first:pt-0" data-testid={testid}>
        <span className="text-sm text-foreground">{label}</span>
        <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> baseline captured
        </span>
      </div>
    );
  }
  const meta = directionMeta(deltaPct, !!upIsGood);
  const Icon = meta.arrow;
  return (
    <div className="flex items-center justify-between pt-3 first:pt-0" data-testid={testid}>
      <span className="text-sm text-foreground">{label}</span>
      <span className={`text-sm font-medium inline-flex items-center gap-1.5 ${toneClasses(meta.tone)}`}>
        <Icon className="h-4 w-4" />
        {meta.label}
      </span>
    </div>
  );
}

function InvestingRow({ deltaPct, testid }) {
  let label = 'you kept investing';
  let tone = 'positive';
  let Icon = Minus;
  if (deltaPct != null && deltaPct > 0.5) {
    label = `stepped up ${deltaPct.toFixed(1)}%`;
    Icon = ArrowUpRight;
  } else if (deltaPct != null && deltaPct < -0.5) {
    label = `eased down ${Math.abs(deltaPct).toFixed(1)}%`;
    Icon = ArrowDownRight;
    tone = 'muted';
  }
  return (
    <div className="flex items-center justify-between pt-3" data-testid={testid}>
      <span className="text-sm text-foreground">Investing</span>
      <span className={`text-sm font-medium inline-flex items-center gap-1.5 ${toneClasses(tone)}`}>
        <Icon className="h-4 w-4" />
        {label}
      </span>
    </div>
  );
}

function SavingsRow({ rate, testid }) {
  let label; let tone;
  if (rate >= 20) {
    label = `~${Math.round(rate / 5) * 5}% of income saved`;
    tone = 'positive';
  } else if (rate >= 5) {
    label = `~${Math.round(rate / 5) * 5}% saved`;
    tone = 'muted';
  } else {
    label = 'tight month on savings';
    tone = 'muted';
  }
  return (
    <div className="flex items-center justify-between pt-3" data-testid={testid}>
      <span className="text-sm text-foreground">Savings rate</span>
      <span className={`text-sm font-medium inline-flex items-center gap-1.5 ${toneClasses(tone)}`}>
        {label}
      </span>
    </div>
  );
}

// --- THIN ------------------------------------------------------------------
function ThinView({ brief, monthStr, reflectionUrl, dashboardUrl }) {
  const data = brief.data || {};
  const currency = data.currency || 'INR';
  return (
    <>
      <Card className="rounded-2xl border-border/70" data-testid="monthly-thin">
        <CardContent className="p-5 space-y-3">
          <div className="text-[11px] uppercase tracking-wide text-gold">What we saw</div>
          <div className="text-sm">
            You logged <strong>{data.txn_count || 0}</strong> transaction{(data.txn_count || 0) === 1 ? '' : 's'} in {monthLabelFromStr(monthStr)}.
          </div>
          <div className="text-xs text-muted-foreground">
            Income tracked: {fmtCurrency(data.total_income, currency)} · Expenses tracked: {fmtCurrency(data.total_expenses, currency)}
          </div>
          <div className="pt-3 mt-3 border-t border-border/50">
            <div className="text-[11px] uppercase tracking-wide text-gold mb-2">What you&rsquo;d unlock with more data</div>
            <ul className="text-sm text-muted-foreground space-y-1 leading-relaxed">
              <li>• Savings-rate breakdown for the month</li>
              <li>• Top 3 spending categories with percentages</li>
              <li>• Goal progress across all your goals</li>
              <li>• A month-over-month look at how your net worth shifted</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      {brief.question?.text && (
        <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg">
          <CardContent className="p-6 space-y-3">
            <div className="text-[11px] uppercase tracking-[2px] text-gold">Reflection prompt</div>
            <div className="font-display text-xl leading-snug">{brief.question.text}</div>
          </CardContent>
        </Card>
      )}
      <div className="flex flex-col items-center gap-3 pt-1">
        <Button asChild size="lg" className="rounded-xl" data-testid="monthly-thin-cta-reflection">
          <a href={reflectionUrl}>
            <BookOpen className="h-4 w-4 mr-2" />
            Record your reflection
          </a>
        </Button>
        <a
          href={dashboardUrl}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          data-testid="monthly-thin-cta-dashboard"
        >
          <LayoutDashboard className="h-3.5 w-3.5" />
          Open your dashboard
        </a>
      </div>
    </>
  );
}

// --- EMPTY (onboarding-first) ---------------------------------------------
function EmptyView({ brief }) {
  return (
    <>
      <Card className="rounded-2xl border-dashed border-border/70 bg-muted/30" data-testid="monthly-empty-preview">
        <CardContent className="p-5">
          <div className="text-[11px] uppercase tracking-wide text-gold mb-3">Preview · what your recap will look like</div>
          <div className="grid grid-cols-3 gap-3">
            <PlaceholderTile label="Income" />
            <PlaceholderTile label="Expenses" />
            <PlaceholderTile label="Savings rate" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-1.5 rounded-full bg-muted" />
            <div className="h-1.5 rounded-full bg-muted" />
            <div className="h-1.5 rounded-full bg-muted" />
          </div>
          <div className="text-xs text-muted-foreground italic mt-3">Add a bit of data and the numbers start filling in.</div>
        </CardContent>
      </Card>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-gold mb-3">Three steps to unlock your recap</div>
        <div className="space-y-2">
          <StepRow icon={PlusCircle} title="Link an account or add transactions"
                   body="Connect your first account or add 5 transactions manually — that unlocks the income / expenses / savings-rate view above."
                   href="/transactions?add=1" primary
                   testid="monthly-empty-cta-transactions" />
          <StepRow icon={Sparkles} title="Add 5 transactions manually"
                   body="Prefer to skip account-linking for now? Add just five recent transactions and the recap starts to take shape."
                   href="/transactions?add=1"
                   testid="monthly-empty-cta-manual" />
          <StepRow icon={User} title="Complete your Life Profile"
                   body="A few questions about your life shape what 2120 shows you — goals, priorities, income stability. About 2 minutes."
                   href="/settings#life-profile"
                   testid="monthly-empty-cta-life-profile" />
        </div>
      </div>

      {brief.question?.text && (
        <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg">
          <CardContent className="p-6 space-y-3">
            <div className="text-[11px] uppercase tracking-[2px] text-gold">A gentle question to sit with</div>
            <div className="font-display text-xl leading-snug">{brief.question.text}</div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function ReengageView() {
  return (
    <Card className="rounded-3xl border-0 bg-brand-gradient text-primary-foreground shadow-brand-lg" data-testid="monthly-reengage">
      <CardContent className="p-6 sm:p-8 space-y-4">
        <div className="text-[11px] uppercase tracking-[2px] text-gold flex items-center gap-1.5">
          <Compass className="h-3 w-3" /> A season to check in?
        </div>
        <div className="font-display text-xl leading-snug">We&rsquo;re here whenever you&rsquo;re ready.</div>
        <p className="text-sm text-primary-foreground/70 leading-relaxed">
          When you&rsquo;re ready to check back in — update a few transactions, refresh your Life Profile, or just poke around —
          your monthly recaps and Sunday briefs will resume automatically. Nothing to reactivate.
        </p>
      </CardContent>
    </Card>
  );
}

// --- little building blocks -----------------------------------------------
function PlaceholderTile({ label }) {
  return (
    <div className="rounded-xl bg-muted/50 p-4 text-center">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-lg font-semibold text-muted-foreground/40 mt-1">— — —</div>
    </div>
  );
}
function StepRow({ icon: Icon, title, body, href, primary, testid }) {
  return (
    <Card className={`rounded-2xl transition-colors ${primary ? 'border-primary/40 hover:border-primary' : 'border-border/70 hover:border-border'}`}>
      <CardContent className="p-4">
        <a href={href} className="flex items-start gap-3 group" data-testid={testid}>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${primary ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm group-hover:text-primary transition-colors">{title}</div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</div>
          </div>
        </a>
      </CardContent>
    </Card>
  );
}
